import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Eye, Search, Users } from "lucide-react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import UserDetailDrawer from "../components/UserDetailDrawer";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Skeleton } from "../components/ui/skeleton";
import { FaDiscord } from "react-icons/fa";

export default function Customers() {
  const [customers, setCustomers] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => { const r = await api.get("/customers"); setCustomers(r.data); };
  useEffect(() => { load(); }, []);

  const filtered = customers?.filter((c) => (c.discord_username || "").toLowerCase().includes(q.toLowerCase()) || (c.discord_email || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader
        title="Customers"
        subtitle="Community members who signed in via Discord. Manage their access and sessions."
        action={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <Input data-testid="customer-search" placeholder="Search customers" value={q} onChange={(e) => setQ(e.target.value)} className="w-64 bg-slate-900/60 pl-9" />
          </div>
        }
      />

      {!customers ? (
        <Skeleton className="h-64 rounded-2xl bg-slate-800/50" />
      ) : filtered.length === 0 ? (
        <div className="card-glass flex flex-col items-center rounded-2xl py-16 text-center">
          <Users className="h-10 w-10 text-slate-600" />
          <p className="mt-3 text-sm text-slate-400">No customers yet. Members appear here after signing in with Discord.</p>
        </div>
      ) : (
        <div className="card-glass overflow-hidden rounded-2xl">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3 font-semibold">Member</th>
                <th className="px-5 py-3 font-semibold">Discord Email</th>
                <th className="px-5 py-3 font-semibold">Location</th>
                <th className="px-5 py-3 font-semibold">Last Sign In</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <motion.tr key={c.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }} className="border-b border-slate-800/50 transition-colors hover:bg-slate-800/40" data-testid={`customer-row-${c.id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9 border border-indigo-500/40"><AvatarImage src={c.discord_avatar} /><AvatarFallback className="bg-indigo-600/30 text-xs text-indigo-200">{(c.discord_username || "?").slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
                      <span className="flex items-center gap-1 font-medium text-white"><FaDiscord className="text-indigo-400" /> {c.discord_username}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-slate-400">{c.discord_email || "—"}</td>
                  <td className="px-5 py-3 text-slate-400">{c.last_location || "—"}</td>
                  <td className="px-5 py-3 text-slate-400">{c.last_sign_in ? new Date(c.last_sign_in).toLocaleDateString() : "—"}</td>
                  <td className="px-5 py-3">{c.suspended ? <span className="rounded border border-red-500/30 bg-red-500/10 px-2 py-0.5 text-xs text-red-400">Suspended</span> : <span className="rounded border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-400">Active</span>}</td>
                  <td className="px-5 py-3 text-right"><Button data-testid={`view-customer-${c.id}`} size="sm" variant="secondary" onClick={() => setDetailId(c.id)} className="gap-1"><Eye className="h-3.5 w-3.5" /> View</Button></td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <UserDetailDrawer open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)} userType="customer" userId={detailId} onChanged={load} />
    </div>
  );
}
